// LIFF (LINE 内ブラウザ) では fetch の Set-Cookie が保持されないことがあるため、
// /api/liff/session で受け取ったセッショントークンを localStorage に保持し、
// Bearer として送る。通常の Web ログインは従来どおり cookie (credentials:include)。
export const AUTH_TOKEN_KEY = "cfl_auth_token";
function getStoredToken(): string | null {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}
export function setStoredToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(AUTH_TOKEN_KEY, token);
    else localStorage.removeItem(AUTH_TOKEN_KEY);
  } catch {
    /* localStorage 不可環境では Bearer 併用を諦める (cookie にフォールバック) */
  }
}

// セッション切れ (401) を検知したときの共通ハンドラ。
// AuthProvider が登録し、認証状態のクリア + ログイン画面への遷移を行う。
// /auth/* (ログイン/ログアウト/me 自身) は対象外（自前でエラー表示・遷移ループ防止）。
let unauthorizedHandler: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  unauthorizedHandler = fn;
}

// API クライアント (fetch ラッパ). credentials を必ず付ける。
export class ApiClient {
  constructor(private base = "") {}

  async request<T>(
    path: string,
    init: RequestInit & { json?: unknown } = {},
  ): Promise<T> {
    const headers = new Headers(init.headers);
    if (init.json !== undefined) {
      headers.set("content-type", "application/json");
      init.body = JSON.stringify(init.json);
    }
    const stored = getStoredToken();
    if (stored && !headers.has("authorization")) {
      headers.set("authorization", `Bearer ${stored}`);
    }
    const res = await fetch(this.base + path, {
      ...init,
      headers,
      credentials: "include",
    });
    const text = await res.text();
    let data: unknown = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = text;
      }
    }
    if (!res.ok) {
      // セッション切れ: 認証必須リクエストが 401 を返したらログイン画面へ誘導する。
      // /auth/* 自身は除外（ログイン失敗の表示や me プローブの正常系を壊さないため）。
      // /liff/* も除外。ここは会員 JWT を使わない公開ルートで、401 は
      // 「LINE の ID トークンが無効」という別の意味。会員のセッション切れとして
      // 扱うと、**LIFF の画面から会員ログイン画面へ飛ばされて何も表示できなくなる**。
      if (
        res.status === 401 &&
        !path.startsWith("/auth/") &&
        !path.startsWith("/liff/")
      ) {
        unauthorizedHandler?.();
      }
      const code =
        (data as { error?: { code?: string } } | null)?.error?.code ??
        "http_error";
      const message =
        (data as { error?: { message?: string } } | null)?.error?.message ??
        `HTTP ${res.status}`;
      const e = new Error(message);
      (e as Error & { code?: string; status?: number }).code = code;
      (e as Error & { code?: string; status?: number }).status = res.status;
      throw e;
    }
    return data as T;
  }

  get<T>(p: string) {
    return this.request<T>(p);
  }
  post<T>(p: string, json?: unknown) {
    return this.request<T>(p, { method: "POST", json });
  }
  patch<T>(p: string, json?: unknown) {
    return this.request<T>(p, { method: "PATCH", json });
  }
  delete<T>(p: string, json?: unknown) {
    return this.request<T>(p, { method: "DELETE", json });
  }
}

export const api = new ApiClient("/api");
